import { Accordion } from "@via/components/accordion/accordion";
import {
  ExclamationCircleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import SelectComponent from "@via/components/select/select";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Tooltip } from "react-tooltip";
import {
  usePipelineMenuGroupsList,
  useProcessMenuGroupsList,
  useCreatePipelineMenuGroup,
  useCreateProcessMenuGroup,
} from "@via/pages/projects/pipelines/pipelines.api.tsx";
import {
  CheckImportViaPipelineResponseBody,
  ImportViaPipelineCategories,
  ImportViaPipelineItem,
  MenuGroupType,
  ProcessMenuGroupType,
} from "common";
import { ImportViaPipelineCategoryMapping } from "@via/pages/projects/pipelines/workflow/utils/constants";
import { SelectOption } from "@via/utils/interfaces";

const Status = ({
  id,
  isConflict,
  shortMessage,
  detailedMessage,
  needsDecision = false,
}) => {
  const colorClass = isConflict ? "text-red-600" : "text-green-600";
  const Icon = isConflict ? ExclamationCircleIcon : CheckCircleIcon;

  return (
    <div>
      <div id={`tooltip-icon-${id}`} className="flex items-center gap-1">
        <Icon className={`h-5 w-5 ${colorClass}`} />
        <Tooltip
          anchorSelect={`#tooltip-icon-${id}`}
          content={detailedMessage}
        />

        <span className={colorClass}>{shortMessage}</span>
      </div>
      {/* A revision identity conflict cannot be resolved from this screen. Point at the
          screen where it can be, so the row is not just a dead end with a red icon. */}
      {needsDecision && (
        <div className="text-xs text-gray-600">
          Choose what to do on the Conflicts step.
        </div>
      )}
    </div>
  );
};

// isConflict, not warningToUser: a revision identity conflict has no warning text
// because there is nothing to overwrite, and counting on the text alone left those
// rows out of the ratio while the row itself was blocking the whole import.
const showNonConflictRatio = items => {
  const conflictCount = items.filter(item => item.isConflict).length;
  return `${conflictCount}/${items.length}`;
};

const MenuGroupColumn = ({
  item,
  category,
  onMenuGroupUpdate,
  menuGroupValue,
}) => {
  const [selectedPipelineMenuGroup, setSelectedPipelineMenuGroup] =
    useState<SelectOption | null>(null);
  const [selectedProcessMenuGroup, setSelectedProcessMenuGroup] =
    useState<SelectOption | null>(null);
  const [selectedAIModelMenuGroup, setSelectedAIModelMenuGroup] =
    useState<SelectOption | null>(null);
  const [selectedModelSkillMenuGroup, setSelectedModelSkillMenuGroup] =
    useState<SelectOption | null>(null);

  const {
    data: pipelineMenuGroupsListData,
    isLoading: isPipelineMenuGroupsListLoading,
  } = usePipelineMenuGroupsList(
    category === ImportViaPipelineCategories.modules ||
      category === ImportViaPipelineCategories.pipeline
  );

  const { mutateAsync: createNewPipelineMenuGroup } =
    useCreatePipelineMenuGroup();

  const {
    data: processMenuGroupsListData,
    isLoading: isProcessMenuGroupsListLoading,
  } = useProcessMenuGroupsList(
    category === ImportViaPipelineCategories.process
      ? String(ProcessMenuGroupType.Process)
      : undefined,
    category === ImportViaPipelineCategories.process
  );

  const {
    data: aiModelMenuGroupsListData,
    isLoading: isAIModelMenuGroupsListLoading,
  } = useProcessMenuGroupsList(
    category === ImportViaPipelineCategories.aiModel
      ? String(MenuGroupType.AI_MODEL)
      : undefined,
    category === ImportViaPipelineCategories.aiModel
  );

  const {
    data: modelSkillMenuGroupsListData,
    isLoading: isModelSkillMenuGroupsListLoading,
  } = useProcessMenuGroupsList(
    category === ImportViaPipelineCategories.modelSkill
      ? String(MenuGroupType.MODEL_SKILL)
      : undefined,
    category === ImportViaPipelineCategories.modelSkill
  );

  const { mutateAsync: createNewProcessMenuGroup } =
    useCreateProcessMenuGroup();

  const pipelineMenuGroupOptions = useMemo(() => {
    return (
      pipelineMenuGroupsListData?.data.map(mg => ({
        label: mg.name,
        value: mg.id,
      })) || []
    );
  }, [pipelineMenuGroupsListData]);

  const processMenuGroupOptions = useMemo(() => {
    return (
      processMenuGroupsListData?.data.map(mg => ({
        label: mg.name,
        value: mg.id,
      })) || []
    );
  }, [processMenuGroupsListData]);

  const aiModelMenuGroupOptions = useMemo(() => {
    return (
      aiModelMenuGroupsListData?.data.map(mg => ({
        label: mg.name,
        value: mg.id,
      })) || []
    );
  }, [aiModelMenuGroupsListData]);

  const modelSkillMenuGroupOptions = useMemo(() => {
    return (
      modelSkillMenuGroupsListData?.data.map(mg => ({
        label: mg.name,
        value: mg.id,
      })) || []
    );
  }, [modelSkillMenuGroupsListData]);

  useEffect(() => {
    const selected = pipelineMenuGroupOptions.find(
      mg => mg.label === menuGroupValue
    );
    if (
      !selected &&
      (category === ImportViaPipelineCategories.pipeline ||
        category === ImportViaPipelineCategories.modules)
    ) {
      handleCreatePipelineMenuGroup(menuGroupValue);
    } else {
      setSelectedPipelineMenuGroup(selected);
    }
  }, [pipelineMenuGroupOptions]);

  useEffect(() => {
    const selected = processMenuGroupOptions.find(
      mg => mg.label === menuGroupValue
    );
    if (!selected && category === ImportViaPipelineCategories.process) {
      handleCreateProcessMenuGroup(menuGroupValue);
    } else {
      setSelectedProcessMenuGroup(selected);
    }
  }, [processMenuGroupOptions]);

  useEffect(() => {
    const selected = aiModelMenuGroupOptions.find(
      mg => mg.label === menuGroupValue
    );
    if (!selected && category === ImportViaPipelineCategories.aiModel) {
      handleCreateAIModelMenuGroup(menuGroupValue);
    } else {
      setSelectedAIModelMenuGroup(selected);
    }
  }, [aiModelMenuGroupOptions]);

  useEffect(() => {
    const selected = modelSkillMenuGroupOptions.find(
      mg => mg.label === menuGroupValue
    );
    if (!selected && category === ImportViaPipelineCategories.modelSkill) {
      handleCreateModelSkillMenuGroup(menuGroupValue);
    } else {
      setSelectedModelSkillMenuGroup(selected);
    }
  }, [modelSkillMenuGroupOptions]);

  const handleChangePipelineMenuGroup = selectedOption => {
    setSelectedPipelineMenuGroup(selectedOption);
    onMenuGroupUpdate(item.name, selectedOption.label);
  };

  const handleChangeProcessMenuGroup = selectedOption => {
    setSelectedProcessMenuGroup(selectedOption);
    onMenuGroupUpdate(item.name, selectedOption.label);
  };

  const handleCreatePipelineMenuGroup = async (name: string) => {
    const result = await createNewPipelineMenuGroup({ name });
    if (result) {
      setSelectedPipelineMenuGroup({
        label: result.name,
        value: result.id,
      });
      onMenuGroupUpdate(item.name, result.name);
    }
  };

  const handleCreateProcessMenuGroup = async (name: string) => {
    const result = await createNewProcessMenuGroup({
      name,
      type: ProcessMenuGroupType.Process,
    });
    if (result) {
      setSelectedProcessMenuGroup({
        label: result.name,
        value: result.id,
      });
      onMenuGroupUpdate(item.name, result.name);
    }
  };

  const handleChangeAIModelMenuGroup = selectedOption => {
    setSelectedAIModelMenuGroup(selectedOption);
    onMenuGroupUpdate(item.name, selectedOption.label);
  };

  const handleCreateAIModelMenuGroup = async (name: string) => {
    const result = await createNewProcessMenuGroup({
      name,
      type: ProcessMenuGroupType.AIModel,
    });
    if (result) {
      setSelectedAIModelMenuGroup({
        label: result.name,
        value: result.id,
      });
      onMenuGroupUpdate(item.name, result.name);
    }
  };

  const handleChangeModelSkillMenuGroup = selectedOption => {
    setSelectedModelSkillMenuGroup(selectedOption);
    onMenuGroupUpdate(item.name, selectedOption.label);
  };

  const handleCreateModelSkillMenuGroup = async (name: string) => {
    const result = await createNewProcessMenuGroup({
      name,
      type: ProcessMenuGroupType.ModelSkill,
    });
    if (result) {
      setSelectedModelSkillMenuGroup({
        label: result.name,
        value: result.id,
      });
      onMenuGroupUpdate(item.name, result.name);
    }
  };

  if (
    category === ImportViaPipelineCategories.pipeline ||
    category === ImportViaPipelineCategories.modules
  ) {
    return (
      <SelectComponent
        name="menuGroup"
        options={pipelineMenuGroupOptions}
        isLoading={isPipelineMenuGroupsListLoading}
        value={selectedPipelineMenuGroup}
        onChange={handleChangePipelineMenuGroup}
        isCreatable
        onCreateOption={handleCreatePipelineMenuGroup}
        placeholder={"Select or Create a Menu Group"}
        enableClear={false}
      />
    );
  }

  if (category === ImportViaPipelineCategories.process) {
    return (
      <SelectComponent
        name="menuGroup"
        options={processMenuGroupOptions}
        isLoading={isProcessMenuGroupsListLoading}
        value={selectedProcessMenuGroup}
        onChange={handleChangeProcessMenuGroup}
        isCreatable
        onCreateOption={handleCreateProcessMenuGroup}
        placeholder={"Select or Create a Menu Group"}
        enableClear={false}
      />
    );
  }

  if (category === ImportViaPipelineCategories.app) {
    return <div></div>;
  }

  if (category === ImportViaPipelineCategories.aiModel) {
    return (
      <SelectComponent
        name="menuGroup"
        options={aiModelMenuGroupOptions}
        isLoading={isAIModelMenuGroupsListLoading}
        value={selectedAIModelMenuGroup}
        onChange={handleChangeAIModelMenuGroup}
        isCreatable
        onCreateOption={handleCreateAIModelMenuGroup}
        placeholder={"Select or Create a Menu Group"}
        enableClear={false}
      />
    );
  }

  if (category === ImportViaPipelineCategories.modelSkill) {
    return (
      <SelectComponent
        name="menuGroup"
        options={modelSkillMenuGroupOptions}
        isLoading={isModelSkillMenuGroupsListLoading}
        value={selectedModelSkillMenuGroup}
        onChange={handleChangeModelSkillMenuGroup}
        isCreatable
        onCreateOption={handleCreateModelSkillMenuGroup}
        placeholder={"Select or Create a Menu Group"}
        enableClear={false}
      />
    );
  }

  return null;
};

const PipelineSummary = ({
  summary,
  onMenuGroupUpdate,
  menuGroupUpdates,
}: {
  summary: CheckImportViaPipelineResponseBody | null;
  onMenuGroupUpdate: (
    category: ImportViaPipelineCategories,
    itemId: string,
    newMenuGroup: string
  ) => void;
  menuGroupUpdates: Record<string, ImportViaPipelineItem[]>;
}) => {
  return (
    <>
      <div className="my-4 px-2 flex justify-between">
        <div>
          <span className="font-semibold">File Name: </span>
          <span>{summary?.fileId}</span>
        </div>
      </div>
      {summary?.categories
        ?.filter(category => category.items?.length > 0)
        .map(category => (
          <Accordion
            key={category.category}
            initialOpen
            title={`${ImportViaPipelineCategoryMapping[category.category]} (${showNonConflictRatio(category.items)})`}>
            <div className="grid grid-cols-3 gap-4 items-center">
              <div className="font-bold text-sm">Name</div>
              {category.category === ImportViaPipelineCategories.app ? (
                <div></div>
              ) : (
                <div className="font-bold text-sm">Menu Group</div>
              )}
              <div className="font-bold text-sm">Status</div>
              {category.items.map((item: any) => (
                <Fragment key={item.name}>
                  <div>{item.name}</div>
                  <MenuGroupColumn
                    item={item}
                    category={category?.category}
                    onMenuGroupUpdate={(_, newMenuGroup) =>
                      onMenuGroupUpdate(
                        category.category,
                        item.id,
                        newMenuGroup
                      )
                    }
                    menuGroupValue={
                      menuGroupUpdates?.[category.category]?.find(
                        mg => mg.id === item.id
                      )?.menuGroupName ||
                      item.menuGroup ||
                      ""
                    }
                  />
                  <Status
                    id={item.id}
                    isConflict={Boolean(item.isConflict)}
                    shortMessage={item.shortMessage}
                    detailedMessage={item.detailedMessage}
                    needsDecision={Boolean(item.revisionConflict)}
                  />
                </Fragment>
              ))}
            </div>
          </Accordion>
        ))}
    </>
  );
};

export default PipelineSummary;
